import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { ANALYTICS } from './analyticsTokens';
import { TeacherReadiness } from './TeacherReadiness';
import { Chevron, SisCoverage } from './SisCoverage';
import {
  checked,
  describeGaps,
  describeKind,
  describeLanding,
  describeOpenable,
  describeSources,
  describeCells,
  describeColumns,
  describeFeed,
  describeFlashlightRefusal,
  describeVerdict,
  describeWida,
  fetchProgressReportAudit,
  flashlightSide,
  formatRunAt,
  OUTCOME_LABELS,
  rosterStreamSide,
  share,
  sourceGrid,
  splitColumns,
  splitMatches,
  splitTotals,
  tiers,
  trialNames,
  withoutFeed,
  type AuditDistrict,
  type AuditReport,
  type AuditVariant,
  type MatchSplit,
  type Outcome,
  type VariantGroup,
  VARIANT_COLUMNS,
} from './progressReportAuditApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Props = {
  source?: () => Promise<AuditReport>;
};

const base = { fontFamily: ANALYTICS.font } as const;

const bodyText = { ...base, fontSize: 16, lineHeight: '24px', color: ANALYTICS.muted } as const;

const cardSx = {
  bgcolor: ANALYTICS.surface,
  borderRadius: '8px',
  border: `1px solid ${ANALYTICS.grey50}`,
} as const;

const BUCKETS: { key: Outcome; label: string; meaning: string }[] = [
  {
    key: 'onRoster',
    label: OUTCOME_LABELS.onRoster,
    meaning: 'Flashlight360 found the student and lists them on the district\'s roster today. A teacher can open them.',
  },
  {
    key: 'pastOnly',
    label: OUTCOME_LABELS.pastOnly,
    meaning: 'Flashlight360 found the student, usually in an earlier year\'s database, but they are not on the district\'s roster today. Their report shows what that record holds and nothing newer.',
  },
  {
    key: 'none',
    label: OUTCOME_LABELS.none,
    meaning: 'No Flashlight360 database knows the student under any id the district\'s data holds for them.',
  },
  {
    key: 'unanswered',
    label: OUTCOME_LABELS.unanswered,
    meaning: 'The lookup did not answer for the student, or they were never sent: an ambiguous key, or the run stopped first.',
  },
];

const OUTCOMES: { key: string; label: string; meaning: string }[] = [
  {
    key: 'students',
    label: 'Students',
    meaning: 'Students the Progress Reports page lists: one per roster student, departed students left out.',
  },
  {
    key: 'resolved',
    label: 'Found in FL360',
    meaning: 'A lookup found the student in at least one database and none answered an error. The report prints with whatever data was found.',
  },
  {
    key: 'notFound',
    label: 'Not found',
    meaning: 'No lookup found a Flashlight360 record under any id the roster holds for the student, in any database. The report prints with no Flashlight data for this student.',
  },
  {
    key: 'foundByAltKey',
    label: 'Found by other ID',
    meaning: 'Of the matched: Flashlight360 holds the student only under another id the roster has for them, such as the district id when the report key is the state id. The report finds them by that id today; fixing the key would make the match direct.',
  },
  {
    key: 'errored',
    label: 'Errored',
    meaning: 'A Flashlight360 database answered an error for the student. The report route refuses to print (502) until it succeeds.',
  },
  {
    key: 'unattempted',
    label: 'Unattempted',
    meaning: 'The whole batch failed before any student was looked up, usually because the district has no resolver plan configured.',
  },
  {
    key: 'ambiguous',
    label: 'Ambiguous',
    meaning: 'The Progress Reports page cannot offer this student: their key also names another student\'s record, or two students share one report key, so the report route would answer 404. Not sent to Flashlight360.',
  },
];

export function AdminProgressReportAuditPage({ source = fetchProgressReportAudit }: Props = {}) {
  const [report, setReport] = useState<AuditReport | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setReport(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const ready = !failed && report !== null && report.computed;
  const graded = ready && report.districts.some((row) => row.readiness);
  const covered = ready && report.districts.some((row) => row.coverage);
  const tabs = availableTabs(graded, covered);
  const [chosen, setChosen] = useState<TabKey | null>(null);
  const focused = useRef<string | null>(null);
  const tab: TabKey = chosen !== null && tabs.some((item) => item.key === chosen) ? chosen : (tabs[0]?.key ?? 'details');
  const showDetails = tab === 'details';

  useEffect(() => {
    const districtId = focused.current;
    if (!districtId || !showDetails) return;
    focused.current = null;
    const card = Array.from(document.querySelectorAll('[data-testid="audit-row"]')).find((row) => row.getAttribute('data-district') === districtId);
    if (card && typeof card.scrollIntoView === 'function') card.scrollIntoView();
  }, [showDetails]);

  const openDetails = (districtId: string) => {
    focused.current = districtId;
    setChosen('details');
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: ANALYTICS.background, minHeight: HEIGHT_BELOW_BARS }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          gap: '32px',
          p: { xs: '40px 24px 56px', lg: '40px 100px 56px' },
        }}
      >
        <PageHeader report={ready ? report : null} />
        {tabs.length > 0 && <Tabs tabs={tabs} tab={tab} onTab={setChosen} />}
        {showDetails && <Explainer />}

        {failed && (
          <Typography data-testid="audit-error" sx={bodyText}>
            Could not load the audit. Refresh to try again.
          </Typography>
        )}
        {!failed && report === null && <CircularProgress data-testid="audit-loading" size={24} />}
        {!failed && report !== null && !report.computed && (
          <Typography data-testid="audit-not-run" sx={bodyText}>
            The audit has not run yet. It runs every day at 09:00 UTC (2 AM MST, 3 AM MDT), and can be run by hand
            from the adhoc-progress-report-audit Lambda.
          </Typography>
        )}
        {ready && report.incomplete && (
          <Typography data-testid="audit-incomplete" sx={{ ...bodyText, color: ANALYTICS.red600, fontWeight: 700 }}>
            Not every student was audited: a shard of the run is still out, the run reached its time limit, a
            district&apos;s roster could not be classified, or students were left unattempted when a batch failed. Those
            districts carry a note; where an earlier audit exists its counts are still shown with the date they come
            from. Running the audit again with districtIds set to those districts fills them into this report; a run
            without districtIds replaces it.
          </Typography>
        )}
        {ready && report.configsUnknown && (
          <Typography data-testid="audit-configs-unknown" sx={{ ...bodyText, color: ANALYTICS.red600, fontWeight: 700 }}>
            The last full run did not list the districts the Flashlight360 resolver is configured for, because it could
            not or because it ran before the audit asked, so districts that are configured but not yet in Insights are
            missing below, and no card says whether it is configured or names its state.
          </Typography>
        )}
        {ready && report.usersUnknown && (
          <Typography data-testid="audit-users-unknown" sx={{ ...bodyText, color: ANALYTICS.red600, fontWeight: 700 }}>
            The last full run did not list the people with Insights access, so districts with users but no
            roster are missing below and no teacher&apos;s roster was opened.
          </Typography>
        )}
        {ready && showDetails && (
          <>
            <TotalsStrip report={report} />
            <DistrictCards districts={report.districts} />
          </>
        )}
        {ready && tab === 'readiness' && <TeacherReadiness districts={report.districts} />}
        {ready && tab === 'coverage' && <SisCoverage districts={report.districts} onOpenDetails={openDetails} />}
      </Box>
    </Box>
  );
}

type TabKey = 'readiness' | 'coverage' | 'details';

type Tab = { key: TabKey; label: string };

function availableTabs(graded: boolean, covered: boolean): Tab[] {
  if (!graded && !covered) return [];
  return [
    ...(graded ? [{ key: 'readiness', label: 'Teacher readiness' } as Tab] : []),
    ...(covered ? [{ key: 'coverage', label: 'SIS data coverage' } as Tab] : []),
    { key: 'details', label: 'District details' },
  ];
}

function Tabs({ tabs, tab, onTab }: { tabs: Tab[]; tab: TabKey; onTab: (tab: TabKey) => void }) {
  return (
    <Box role="tablist" sx={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${ANALYTICS.grey50}` }}>
      {tabs.map((item) => {
        const selected = item.key === tab;
        return (
          <Box
            key={item.key}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={`audit-tab-${item.key}`}
            onClick={() => onTab(item.key)}
            sx={{
              ...base,
              fontSize: 14,
              lineHeight: '20px',
              fontWeight: selected ? 700 : 400,
              color: selected ? ANALYTICS.ink : ANALYTICS.muted,
              background: 'none',
              border: 0,
              borderBottom: `2px solid ${selected ? ANALYTICS.primary : 'transparent'}`,
              p: '10px 16px',
              cursor: 'pointer',
            }}
          >
            <Box component="span" data-testid="audit-tab">
              {item.label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function PageHeader({ report }: { report: AuditReport | null }) {
  return (
    <Box>
      <Typography
        component="h1"
        sx={{
          ...base,
          fontSize: 32,
          lineHeight: '40px',
          fontWeight: 800,
          letterSpacing: ANALYTICS.tracking.tight,
          color: ANALYTICS.ink,
        }}
      >
        Progress Report Audit
      </Typography>
      {report?.runAt && (
        <Typography
          data-testid="audit-run-at"
          sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: ANALYTICS.muted, mt: '4px' }}
        >
          Last run {formatRunAt(report.runAt)}
          {report.trigger ? ` (${report.trigger})` : ''}
          {report.kind ? `, ${describeKind(report.kind)}` : ''}
        </Typography>
      )}
    </Box>
  );
}

function Explainer() {
  const term = { ...base, fontSize: 14, lineHeight: '20px', fontWeight: 700, color: ANALYTICS.ink } as const;
  const meaning = { ...base, m: 0, fontSize: 14, lineHeight: '20px', color: ANALYTICS.muted } as const;
  const [open, setOpen] = useState(false);
  return (
    <Box data-testid="audit-explainer" sx={{ ...cardSx, p: '24px' }}>
      <Typography component="h2" sx={{ m: 0 }}>
        <Box
          component="button"
          type="button"
          data-testid="audit-explainer-toggle"
          aria-expanded={open}
          aria-controls="audit-explainer-body"
          onClick={() => setOpen(!open)}
          sx={{
            ...base,
            fontSize: 20,
            lineHeight: '28px',
            fontWeight: 700,
            color: ANALYTICS.ink,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            background: 'none',
            border: 0,
            p: 0,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          What this audit checks
          <Chevron open={open} height={28} />
        </Box>
      </Typography>
      {open && (
        <Box id="audit-explainer-body" data-testid="audit-explainer-body" sx={{ mt: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography sx={bodyText}>
            Every student in every district&apos;s uploaded files is sent to Flashlight360 the same way the Student
            Progress Report does, using the student&apos;s WIDA key when the roster has one and the roster key otherwise.
            A student who resolves can get a report with Flashlight data; a student who does not will get a report
            with gaps, or none at all.
          </Typography>
          <Typography sx={bodyText}>
            Each district is read both ways round. <strong>District students found in FL360</strong> asks how many of the
            students in the district&apos;s data Flashlight360 has any record of. <strong>FL360 students found in district
            data</strong> asks the reverse: how many of the students Flashlight360 lists for the district appear in its
            data. A district can score high one way and low the other, when its data lists far more students than use
            Flashlight360, or far fewer. The verdict on each card says which it is.
          </Typography>
          <Typography sx={bodyText}>
            Every district the Flashlight360 resolver holds plans for is listed, even one nobody has uploaded for or can
            sign in to, so the page shows the whole estate in three tiers: with data, with people but no data, and
            configured only. A district with data and no plans is flagged, since every lookup for it fails. Each card
            names the district&apos;s state and whether it is a WIDA consortium state, which decides whether the WIDA
            file and ACCESS scores apply.
          </Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '16px', rowGap: '8px' }}>
            {[...BUCKETS, ...OUTCOMES].map((outcome) => (
              <Box key={outcome.key} sx={{ display: 'contents' }}>
                <Typography component="dt" sx={term}>
                  {outcome.label}
                </Typography>
                <Typography component="dd" sx={meaning}>
                  {outcome.meaning}
                </Typography>
              </Box>
            ))}
            <Typography component="dt" sx={term}>
              Failed
            </Typography>
            <Typography component="dd" sx={meaning}>
              Not found, errored, unattempted and ambiguous together: every student whose progress report cannot show Flashlight data today.
            </Typography>
          </Box>
          <Typography sx={{ ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.disabled }}>
            Each district shows how many of its students each source holds: From uploads is the students an uploaded
            SIS or WIDA file names, From RosterStream is the students the roster integration lists, From Flashlight360
            is the students on the roster that Flashlight360 lists, the ones its list added included; students held back
            for want of links are not counted there (FL360 students is its whole list), and a student in several sources
            counts in each. FL360 students is how many students Flashlight360 lists under the district, active
            and dropped from its roster; FL360 students matched is how many distinct ones the found rows are, two rows
            that resolve to one student counting once. A dash means the district was audited before these were counted,
            or the resolver could not say. The headline FL360 rate is over the districts that have data; the ones
            without are named below it. Every district with a live uploaded file or a rosterstream feed is audited, and
            so is every district where someone has Insights access when the run could list them; deleted uploads and
            super-admin sandboxes are not. Each student is counted once by where the roster knew them from:
            rosterstream, an uploaded SIS file, the WIDA file alone, or Flashlight360 alone. FL360 students matched to
            RosterStream counts, over the Insight districts with a bound feed, the active FL360 students the audit matched
            from a feed record; Insight districts without RosterStream counts the rest; both leave out test districts and
            districts whose current FL360 name says trial, which stay everywhere else on the page.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const tileLabelSx = { ...base, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink } as const;
const captionSx = { ...base, fontSize: 12, lineHeight: '16px', color: ANALYTICS.muted } as const;
const lineSx = { ...base, fontSize: 14, lineHeight: '20px', color: ANALYTICS.muted } as const;

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

function byFlashlightCount(a: AuditDistrict, b: AuditDistrict): number {
  return (b.flashlightStudents ?? -1) - (a.flashlightStudents ?? -1);
}

function withCount(rows: AuditDistrict[]): string {
  if (!rows.every((row) => row.flashlightStudents !== undefined)) return '';
  return ` with ${rows.reduce((sum, row) => sum + (row.flashlightStudents ?? 0), 0)} active FL360 students`;
}

function named(rows: AuditDistrict[]): string {
  return rows.map((row) => `${row.districtName || row.districtId} ${row.flashlightStudents ?? '—'}`).join(' · ');
}

function describeState(row: AuditDistrict): string | null {
  if (row.wida === undefined) return row.state ? `State: ${row.state}` : row.audited ? 'State unknown' : null;
  return row.wida ? `WIDA state (${row.state})` : `Not a WIDA state (${row.state})`;
}

function splitSummary(split: MatchSplit | null): string {
  if (!split || !split.rosterKnown) return '';
  return ` · ${split.counts.onRoster} on an FL360 roster today, ${split.counts.pastOnly} a past record only`;
}

function flashlightCaption(districts: AuditDistrict[]): { figure: string; caption: string | undefined } {
  const active = flashlightSide(districts, 'active');
  const all = flashlightSide(districts, 'all');
  const lead = active.districts > 0 ? active : all;
  const noun = active.districts > 0 ? 'active FL360 students' : 'FL360 students, current or dropped,';
  const pct = lead.districts > 0 ? share(lead.matched, lead.of) : null;
  const parts: string[] = [];
  if (lead.districts > 0) parts.push(`${lead.matched} of ${lead.of} ${noun} in the ${plural(lead.districts, 'district')} with data`);
  if (lead.skipped > 0) {
    parts.push(lead.districts > 0 ? `${lead.skipped} more without a matched count` : `${plural(lead.skipped, 'district')} with data ${lead.skipped === 1 ? 'has' : 'have'} no matched count`);
  }
  const both = flashlightSide(districts, 'all', true);
  if (active.districts > 0 && both.districts > 0) {
    parts.push(`incl. dropped ${both.matched} of ${both.of}${both.districts < active.districts ? ` in ${both.districts} of them` : ''}`);
  }
  return { figure: pct === null ? '—' : `${pct}%`, caption: parts.length > 0 ? parts.join(' · ') : undefined };
}

function trialNote(trials: number): string {
  return `${plural(trials, 'trial district')} left out`;
}

function rosterStreamCaption(districts: AuditDistrict[]): { figure: string; caption: string | undefined } {
  const rate = rosterStreamSide(districts);
  const parts: string[] = [];
  if (rate.districts > 0) parts.push(`${rate.matched} of ${rate.of} active FL360 students in the ${plural(rate.districts, 'district')} with a RosterStream feed`);
  if (rate.skipped > 0) {
    parts.push(rate.districts > 0 ? `${rate.skipped} more without a matched count` : `${plural(rate.skipped, 'district')} with a feed ${rate.skipped === 1 ? 'has' : 'have'} no matched count`);
  }
  if (rate.fed === 0) parts.push('No Insight district has a RosterStream feed');
  if (rate.trials > 0) parts.push(trialNote(rate.trials));
  const pct = rate.districts > 0 ? share(rate.matched, rate.of) : null;
  return { figure: pct === null ? '—' : `${pct}%`, caption: parts.length > 0 ? parts.join(' · ') : undefined };
}

function withoutFeedCaption(rows: AuditDistrict[], trials: number): string | undefined {
  const parts: string[] = [];
  if (rows.length > 0 && rows.every((row) => row.flashlightStudents !== undefined)) {
    parts.push(`${rows.reduce((sum, row) => sum + (row.flashlightStudents ?? 0), 0)} active FL360 students in them`);
  }
  if (trials > 0) parts.push(trialNote(trials));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

function TotalsStrip({ report }: { report: AuditReport }) {
  const { totals, districts } = report;
  const unfed = withoutFeed(districts);
  const trials = trialNames(districts);
  const resolvedShare = share(totals.resolved, checked(totals));
  const sources = describeSources(totals.sources);
  // Rows audited before sources were recorded carry none; say so rather than
  // present a partial breakdown as the whole.
  const unsourced = totals.students - Object.values(totals.sources).reduce((sum, n) => sum + n, 0);
  const tier = tiers(districts);
  const noData = [...tier.usersOnly, ...tier.bare].sort(byFlashlightCount);
  const configuredOnly = [...tier.configuredOnly].sort(byFlashlightCount);
  const wida = describeWida(districts, !report.configsUnknown);
  const districtParts = [`${tier.data.length} with data`, `${tier.usersOnly.length} users, no data`, `${tier.configuredOnly.length} configured only`];
  if (tier.bare.length > 0) districtParts.push(`${tier.bare.length} without data`);
  if (totals.unreached > 0) districtParts.push(`${totals.unreached} listed but not audited`);
  const tiles = [
    {
      key: 'districtSide',
      label: 'District students found in FL360',
      figure: resolvedShare === null ? '—' : `${resolvedShare}%`,
      caption: `${totals.resolved} of ${checked(totals)} students in district data have a Flashlight360 record${splitSummary(splitTotals(districts))}`,
    },
    { key: 'flashlightSide', label: 'FL360 students found in district data', ...flashlightCaption(districts) },
    { key: 'rosterStreamSide', label: 'FL360 students matched to RosterStream', ...rosterStreamCaption(districts) },
    { key: 'withoutRosterStream', label: 'Insight districts without RosterStream', figure: `${unfed.rows.length} of ${unfed.insight.length}`, caption: withoutFeedCaption(unfed.rows, trials.length) },
    { key: 'districts', label: 'Districts audited', figure: String(totals.districts), caption: districtParts.join(' · ') },
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '24px', flexWrap: 'wrap' }}>
        {tiles.map((tile) => (
          <Box
            key={tile.key}
            data-testid="audit-tile"
            sx={{ ...cardSx, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 160, gap: '6px', p: '24px' }}
          >
            <Typography
              sx={{
                ...base,
                fontSize: 40,
                lineHeight: '44px',
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                color: tile.key === 'districts' ? ANALYTICS.ink : ANALYTICS.primary,
              }}
            >
              {tile.figure}
            </Typography>
            <Typography sx={tileLabelSx}>{tile.label}</Typography>
            {tile.caption && <Typography sx={captionSx}>{tile.caption}</Typography>}
          </Box>
        ))}
      </Box>
      <Typography data-testid="audit-counts" sx={lineSx}>
        Students in district data {totals.students} · found {totals.resolved} · failed {totals.failed}: {totals.notFound} not found, {totals.errored}{' '}
        errored, {totals.unattempted} unattempted, {totals.ambiguous} ambiguous
      </Typography>
      {(sources || unsourced > 0) && (
        <Typography data-testid="audit-sources" sx={lineSx}>
          Students by source: {sources || 'none recorded'}
          {unsourced > 0 ? ` · ${unsourced} audited before sources were recorded` : ''}
        </Typography>
      )}
      {noData.length > 0 && (
        <Typography data-testid="audit-no-data" sx={lineSx}>
          {plural(noData.length, 'district')}
          {withCount(noData)} {noData.length === 1 ? 'has' : 'have'} no data in Insights: {named(noData)}
        </Typography>
      )}
      {unfed.rows.length > 0 && (
        <Typography data-testid="audit-without-feed" sx={lineSx}>
          {plural(unfed.rows.length, 'Insight district')}
          {withCount(unfed.rows)} {unfed.rows.length === 1 ? 'has' : 'have'} no RosterStream feed: {named(unfed.rows)}
        </Typography>
      )}
      {trials.length > 0 && (
        <Typography data-testid="audit-trials" sx={lineSx}>
          Left out of the RosterStream figures as {trials.length === 1 ? 'a trial' : 'trials'}, by FL360 name: {trials.join(' · ')}
        </Typography>
      )}
      {configuredOnly.length > 0 && (
        <Typography data-testid="audit-configured-only" sx={lineSx}>
          {plural(configuredOnly.length, 'configured district')}
          {withCount(configuredOnly)} {configuredOnly.length === 1 ? 'has' : 'have'} nobody in Insights yet: {named(configuredOnly)}
        </Typography>
      )}
      {wida && (
        <Typography data-testid="audit-wida" sx={lineSx}>
          {wida}
        </Typography>
      )}
    </Box>
  );
}

const COLUMNS: { key: keyof AuditDistrict; label: string }[] = [
  { key: 'uploads', label: 'Uploads' },
  { key: 'students', label: 'Students in district data' },
  { key: 'fromUploads', label: 'From uploads' },
  { key: 'fromRosterStream', label: 'From RosterStream' },
  { key: 'fromFlashlight', label: 'From Flashlight360' },
  { key: 'flashlightStudents', label: 'FL360 students' },
  { key: 'flashlightMatched', label: 'FL360 students matched' },
  { key: 'resolved', label: 'Found in FL360' },
  { key: 'notFound', label: 'Not found' },
  { key: 'foundByAltKey', label: 'Found by other ID' },
  { key: 'errored', label: 'Errored' },
  { key: 'unattempted', label: 'Unattempted' },
  { key: 'ambiguous', label: 'Ambiguous' },
  { key: 'failed', label: 'Failed' },
];

function activeAndDropped(active: number | undefined, all: number | undefined): string {
  if (active !== undefined && all !== undefined) return `${active} active, ${all - active} dropped`;
  if (active !== undefined) return `${active} active`;
  if (all !== undefined) return `${all} current or dropped`;
  return '—';
}

function cellText(row: AuditDistrict, key: keyof AuditDistrict): string {
  if (key === 'flashlightStudents') return activeAndDropped(row.flashlightStudents, row.flashlightStudentsAll);
  if (key === 'flashlightMatched') return activeAndDropped(row.flashlightMatchedActive, row.flashlightMatched);
  if (key === 'fromUploads' || key === 'fromRosterStream' || key === 'fromFlashlight') {
    const value = row[key];
    return value === undefined ? '—' : String(value);
  }
  return String(row[key]);
}

const statLabelSx = { ...base, fontSize: 14, lineHeight: '20px', color: ANALYTICS.muted } as const;
const statValueSx = { ...base, fontSize: 14, lineHeight: '20px', fontVariantNumeric: 'tabular-nums', textAlign: 'right' } as const;
const statRowSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '16px', py: '6px' } as const;

function districtNote(row: AuditDistrict): string | null {
  const shown = row.skippedAt && row.auditedAt ? ` Showing results from ${formatRunAt(row.auditedAt)}.` : '';
  const notes: string[] = [];
  if (row.error) notes.push(`${row.error}${shown}`);
  else if (row.skippedAt) notes.push(`Not reached by the run at ${formatRunAt(row.skippedAt)}.${shown}`);
  if (row.part && row.part.landed.length > 0 && row.part.landed.length < row.part.of) {
    const covers = row.part.graded ? 'the counts and teacher readiness cover part of the roster' : row.part.students ? 'the counts cover part of the roster' : 'teacher readiness covers part of the teachers';
    notes.push(`Still auditing: ${row.part.landed.length} of ${row.part.of} parts of this run have landed, so ${covers}.`);
  }
  if (row.part && row.part.rosters.length > 1) {
    notes.push(`The parts read different Flashlight360 rosters (as of ${row.part.rosters.join(', ')}), so a student may be counted twice or missed.`);
  }
  const refusal = describeFlashlightRefusal(row.flashlightRefusal);
  if (refusal) notes.push(refusal);
  if (!row.error && !row.skippedAt && row.notFoundSample.length > 0) notes.push(`Not found, for example: ${row.notFoundSample.join(', ')}`);
  return notes.length > 0 ? notes.join(' ') : null;
}

function Stat({ statKey, label, value, valueSx }: { statKey: string; label: string; value: string; valueSx?: object }) {
  return (
    <Box data-testid="audit-stat" data-key={statKey} sx={statRowSx}>
      <Typography component="dt" sx={statLabelSx}>
        {label}
      </Typography>
      <Typography component="dd" data-testid="audit-stat-value" sx={{ ...statValueSx, m: 0, color: ANALYTICS.ink, ...valueSx }}>
        {value}
      </Typography>
    </Box>
  );
}

type RateProps = { statKey: string; testId: string; label: string; pct: number | null; caption: string; low?: boolean };

function Rate({ statKey, testId, label, pct, caption, low }: RateProps) {
  return (
    <Box data-testid="audit-stat" data-key={statKey} sx={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography sx={{ ...statLabelSx, fontWeight: 700, color: ANALYTICS.ink }}>{label}</Typography>
      <Typography
        data-testid={testId}
        data-low={low ? 'true' : undefined}
        sx={{
          ...base,
          fontSize: 28,
          lineHeight: '32px',
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: pct === null ? ANALYTICS.grey100 : low ? ANALYTICS.red600 : ANALYTICS.primary,
        }}
      >
        {pct === null ? '—' : `${pct}%`}
      </Typography>
      <Typography data-testid="audit-rate-caption" sx={captionSx}>
        {caption}
      </Typography>
    </Box>
  );
}

function flashlightRate(row: AuditDistrict): { pct: number | null; caption: string } {
  if (row.audited && row.students === 0) {
    const counts = [
      row.flashlightStudents !== undefined ? `${row.flashlightStudents} active` : '',
      row.flashlightStudentsAll !== undefined ? `${row.flashlightStudentsAll} incl. dropped` : '',
    ].filter(Boolean);
    return { pct: null, caption: [...counts, 'no data to match against'].join(', ') };
  }
  const active = row.flashlightMatchedActive !== undefined && row.flashlightStudents !== undefined ? `${row.flashlightMatchedActive} of ${row.flashlightStudents} active` : '';
  const all = row.flashlightMatched !== undefined && row.flashlightStudentsAll !== undefined ? `${row.flashlightMatched} of ${row.flashlightStudentsAll} incl. dropped` : '';
  const pct = active
    ? share(row.flashlightMatchedActive ?? 0, row.flashlightStudents ?? 0)
    : all
      ? share(row.flashlightMatched ?? 0, row.flashlightStudentsAll ?? 0)
      : null;
  const caption = [active, all].filter(Boolean).join(' · ');
  const listed = row.flashlightStudents !== undefined ? `FL360 lists ${row.flashlightStudents} active; matched count not available` : row.flashlightStudentsAll !== undefined ? `FL360 lists ${row.flashlightStudentsAll} current or dropped; matched count not available` : 'FL360 gave no count';
  return { pct: row.audited ? pct : null, caption: caption || listed };
}

const SEGMENT_COLORS: Record<Outcome, string> = {
  onRoster: ANALYTICS.primary,
  pastOnly: ANALYTICS.grey100,
  found: ANALYTICS.primary,
  none: ANALYTICS.red100,
  unanswered: ANALYTICS.yellow100,
};

function SplitBar({ split }: { split: MatchSplit }) {
  const columns = splitColumns(split);
  return (
    <Box data-testid="audit-split" sx={{ mt: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <Box sx={{ display: 'flex', height: 12, borderRadius: '6px', overflow: 'hidden', bgcolor: ANALYTICS.grey50 }}>
        {columns.map((outcome) => (
          <Box
            key={outcome}
            data-testid="audit-split-segment"
            data-outcome={outcome}
            style={{ width: `${(split.counts[outcome] / split.students) * 100}%` }}
            sx={{ bgcolor: SEGMENT_COLORS[outcome] }}
          />
        ))}
      </Box>
      <Typography data-testid="audit-split-legend" sx={captionSx}>
        {columns.map((outcome) => `${OUTCOME_LABELS[outcome]} ${split.counts[outcome]}`).join(' · ')}
      </Typography>
    </Box>
  );
}

const gridCellSx = { ...base, fontSize: 14, lineHeight: '20px', p: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: ANALYTICS.ink } as const;
const gridHeadSx = { ...gridCellSx, fontWeight: 700, color: ANALYTICS.muted, borderBottom: `1px solid ${ANALYTICS.divider}` } as const;

function SourceGrid({ row, split }: { row: AuditDistrict; split: MatchSplit }) {
  const rows = row.variance ? sourceGrid(row.variance, checked(row)) : [];
  if (rows.length === 0) return null;
  const columns = splitColumns(split);
  return (
    <Box data-testid="audit-grid" component="table" sx={{ mt: '12px', borderCollapse: 'collapse', width: '100%', maxWidth: 640 }}>
      <Box component="thead">
        <Box component="tr" data-testid="audit-grid-head">
          <Box component="th" sx={{ ...gridHeadSx, textAlign: 'left' }}>
            Known from
          </Box>
          {columns.map((outcome) => (
            <Box key={outcome} component="th" sx={gridHeadSx}>
              {OUTCOME_LABELS[outcome]}
            </Box>
          ))}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((line) => (
          <Box key={line.label} component="tr" data-testid="audit-grid-row">
            <Box component="td" sx={{ ...gridCellSx, textAlign: 'left', color: ANALYTICS.muted }}>
              {line.label}
            </Box>
            {columns.map((outcome) => (
              <Box key={outcome} component="td" sx={gridCellSx}>
                {line.counts[outcome]}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function DistrictCards({ districts }: { districts: AuditDistrict[] }) {
  if (districts.length === 0) {
    return (
      <Typography data-testid="audit-empty" sx={bodyText}>
        No district has uploaded files yet.
      </Typography>
    );
  }
  return (
    <Box data-testid="audit-cards" sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {districts.map((row) => {
        const matchRate = row.audited ? share(row.resolved, checked(row)) : null;
        // Below half by the raw counts, not the rounded figure, so 49.5% reads red.
        const lowMatch = matchRate !== null && row.resolved * 2 < checked(row);
        const flashlight = flashlightRate(row);
        const split = row.audited ? splitMatches(row.variance, checked(row)) : null;
        const verdict = describeVerdict(row);
        const note = districtNote(row);
        return (
          <Box key={row.districtId} data-testid="audit-row" data-district={row.districtId} sx={{ ...cardSx, p: '20px 24px' }}>
            <Typography component="h2" sx={{ ...base, fontSize: 18, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink, m: 0 }}>
              {row.districtName || row.districtId}
            </Typography>
            {describeSources(row.sources) && (
              <Typography data-testid="audit-row-sources" sx={captionSx}>
                {describeSources(row.sources)}
              </Typography>
            )}
            {describeState(row) && (
              <Typography data-testid="audit-district-state" sx={captionSx}>
                {describeState(row)}
              </Typography>
            )}
            {row.configured === false && (
              <Typography data-testid="audit-not-configured" sx={{ ...bodyText, mt: '8px', color: ANALYTICS.red600, fontWeight: 700 }}>
                Not configured for the Flashlight360 resolver: every lookup for this district fails until a plan is posted.
              </Typography>
            )}
            {verdict && (
              <Typography data-testid="audit-verdict" sx={{ ...bodyText, mt: '8px', color: ANALYTICS.ink }}>
                {verdict}
              </Typography>
            )}
            {describeFeed(row.rosterStream) && (
              <Typography data-testid="audit-feed" sx={{ ...lineSx, mt: '4px' }}>
                {describeFeed(row.rosterStream)}
              </Typography>
            )}
            {describeColumns(row.rosterColumns).map((line) => (
              <Typography key={line} data-testid="audit-columns-line" sx={{ ...lineSx, mt: '4px' }}>
                {line}
              </Typography>
            ))}
            <Box component="dl" sx={{ m: 0, mt: '12px', pb: '12px', borderBottom: `1px solid ${ANALYTICS.divider}`, display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <Rate statKey="matchRate" testId="audit-match-rate" label="District students found in FL360" pct={matchRate} caption={`${row.resolved} of ${checked(row)}`} low={lowMatch} />
              <Rate statKey="flashlightRate" testId="audit-flashlight-rate" label="FL360 students found in district data" pct={flashlight.pct} caption={flashlight.caption} />
            </Box>
            {split && <SplitBar split={split} />}
            {split && <SourceGrid row={row} split={split} />}
            <Box component="dl" sx={{ m: 0, mt: '8px' }}>
              {COLUMNS.map((column) => (
                <Stat
                  key={column.key}
                  statKey={column.key}
                  label={column.label}
                  value={cellText(row, column.key)}
                  valueSx={column.key === 'failed' && row.failed > 0 ? { fontWeight: 700, color: ANALYTICS.red600 } : undefined}
                />
              ))}
            </Box>
            <TeacherSection row={row} />
            {note && (
              <Typography
                data-testid="audit-note"
                sx={{ ...base, mt: '12px', fontSize: 12, lineHeight: '16px', color: row.error || row.skippedAt ? ANALYTICS.red600 : ANALYTICS.muted }}
              >
                {note}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

const groupTitleSx = { ...base, fontSize: 14, lineHeight: '20px', fontWeight: 700, color: ANALYTICS.ink, m: 0 } as const;
const groupLineSx = { ...base, fontSize: 14, lineHeight: '20px', color: ANALYTICS.muted } as const;

function VariantList({ group, title, variants }: { group: VariantGroup; title: string; variants: AuditVariant[] }) {
  if (variants.length === 0) return null;
  const listed = variants.some((variant) => variant.listed !== undefined);
  const head = ['Students', ...(listed ? ['Teacher can open'] : []), ...VARIANT_COLUMNS[group], 'For example'];
  return (
    <Box data-testid="audit-variants" data-group={group}>
      <Typography component="h4" sx={groupTitleSx}>
        {title}
      </Typography>
      <Box component="table" sx={{ mt: '4px', borderCollapse: 'collapse', width: '100%' }}>
        <Box component="thead">
          <Box component="tr" data-testid="audit-variant-head">
            {head.map((label, i) => (
              <Box key={label} component="th" sx={{ ...gridHeadSx, textAlign: i < (listed ? 2 : 1) ? 'right' : 'left' }}>
                {label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {variants.map((variant) => (
            <Box key={variant.traits.join('|')} component="tr" data-testid="audit-variant">
              <Box component="td" sx={{ ...gridCellSx, fontWeight: 700 }}>
                {variant.students}
              </Box>
              {listed && (
                <Box component="td" sx={gridCellSx}>
                  {variant.listed === undefined ? '—' : variant.listed}
                </Box>
              )}
              {describeCells(group, variant.traits).map((cell, i) => (
                <Box key={i} component="td" sx={{ ...gridCellSx, textAlign: 'left', color: cell === 'none' || cell === '—' ? ANALYTICS.muted : ANALYTICS.ink }}>
                  {cell}
                </Box>
              ))}
              <Box component="td" sx={{ ...gridCellSx, textAlign: 'left', color: ANALYTICS.muted }}>
                {variant.sample.join(', ')}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function TeacherSection({ row }: { row: AuditDistrict }) {
  const v = row.variance;
  if (!row.audited || !v) return null;
  const gaps = describeGaps(v.gaps);
  const openable = describeOpenable(v.report);
  return (
    <Box
      data-testid="audit-teacher-view"
      sx={{ mt: '12px', pt: '12px', borderTop: `1px solid ${ANALYTICS.divider}`, display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      <Typography component="h3" sx={{ ...base, fontSize: 16, lineHeight: '24px', fontWeight: 700, color: ANALYTICS.ink, m: 0 }}>
        What teachers see
      </Typography>
      <Box data-testid="audit-landing">
        <Typography component="h4" sx={groupTitleSx}>
          Landing in Insights
        </Typography>
        {describeLanding(row, v).map((line) => (
          <Typography key={line} data-testid="audit-landing-line" sx={groupLineSx}>
            {line}
          </Typography>
        ))}
      </Box>
      <VariantList group="report" title="Student progress report" variants={v.report} />
      {openable && (
        <Typography data-testid="audit-openable" sx={groupLineSx}>
          {openable}
        </Typography>
      )}
      {gaps && (
        <Typography data-testid="audit-gaps" sx={groupLineSx}>
          Students whose report prints around a hole: {gaps}
        </Typography>
      )}
      <VariantList group="plan" title="Student action plan" variants={v.plan} />
      {v.students < row.attempted && (
        <Typography data-testid="audit-variance-coverage" sx={groupLineSx}>
          These groups cover {v.students} of the {row.attempted} students looked up; the run stopped before the rest.
        </Typography>
      )}
    </Box>
  );
}
